import { z } from "zod";
import { PROJECT_ROLES } from "../roles.js";
import { isoDay, optionalText, uuid } from "./common.js";

export const TASK_STATUSES = ["OPEN", "WORKING", "REVIEW", "CLOSED"] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];
export const TASK_PRIORITIES = ["LOW", "MEDIUM", "HIGH"] as const;
export const RELEASE_STATUSES = ["PLANNED", "IN_PROGRESS", "RELEASED", "DELAYED"] as const;
export const RELEASE_CHANNELS = ["MAJOR", "MINOR", "PATCH", "HOTFIX"] as const;

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  OPEN: "Da fare",
  WORKING: "In corso",
  REVIEW: "In revisione",
  CLOSED: "Fatto",
};

export const projectInputSchema = z.object({
  name: z.string().trim().min(1).max(160),
  description: optionalText(5000),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default("#2456E6"),
  companyId: uuid.nullable().optional(),
  dealId: uuid.nullable().optional(),
  defaultHourlyRateCents: z.number().int().min(0).max(10_000_000).default(0),
  soldHours: z.number().min(0).max(1_000_000).default(0),
  targetMarginPercent: z.number().int().min(0).max(100).default(25),
  weekMode: z.enum(["FULL_WEEK", "WORK_WEEK"]).default("WORK_WEEK"),
  memberIds: z.array(uuid).max(200).default([]),
});
export type ProjectInput = z.infer<typeof projectInputSchema>;
export const projectUpdateSchema = projectInputSchema.partial().extend({ id: uuid, status: z.enum(["ACTIVE", "ARCHIVED"]).optional() });

export const projectMemberSchema = z.object({
  projectId: uuid,
  userId: uuid,
  role: z.enum(PROJECT_ROLES),
  hourlyRateCents: z.number().int().min(0).nullable().optional(),
});

const taskFields = {
  title: z.string().trim().min(1).max(200),
  description: z.string().max(50_000).default(""),
  startDate: isoDay,
  endDate: isoDay,
  status: z.enum(TASK_STATUSES).default("OPEN"),
  priority: z.enum(TASK_PRIORITIES).nullable().optional(),
  estimatedHours: z.number().min(0).max(100_000).default(0),
  soldHours: z.number().min(0).max(100_000).default(0),
  assigneeId: uuid.nullable().optional(),
  parentId: uuid.nullable().optional(),
  sprintId: uuid.nullable().optional(),
  releaseId: uuid.nullable().optional(),
  isMilestone: z.boolean().default(false),
  billable: z.boolean().default(true),
};
const range = (i: { startDate?: string; endDate?: string }) => !i.startDate || !i.endDate || i.endDate >= i.startDate;
const rangeErr = { message: "La fine deve essere uguale o successiva all'inizio", path: ["endDate"] };

export const taskCreateSchema = z.object({ projectId: uuid, ...taskFields }).refine(range, rangeErr);
export type TaskCreateInput = z.infer<typeof taskCreateSchema>;
export const taskUpdateSchema = z
  .object({ id: uuid, ...Object.fromEntries(Object.entries(taskFields).map(([k, v]) => [k, (v as z.ZodTypeAny).optional()])) } as {
    id: typeof uuid;
  } & { [K in keyof typeof taskFields]: z.ZodOptional<(typeof taskFields)[K]> })
  .refine(range, rangeErr);
export type TaskUpdateInput = z.infer<typeof taskUpdateSchema>;
export const taskMoveSchema = z.object({ id: uuid, status: z.enum(TASK_STATUSES), position: z.number() });
export const taskRescheduleSchema = z.object({ id: uuid, startDate: isoDay, endDate: isoDay }).refine(range, rangeErr);
export const taskListSchema = z.object({
  projectId: uuid,
  assigneeId: uuid.optional(),
  status: z.array(z.enum(TASK_STATUSES)).optional(),
  sprintId: uuid.optional(),
  from: isoDay.optional(),
  to: isoDay.optional(),
});

export const timeEntrySchema = z.object({ taskId: uuid, date: isoDay, minutes: z.number().int().min(1).max(24 * 60), note: optionalText(500) });
export const timeEntryUpdateSchema = z.object({ id: uuid, date: isoDay, minutes: z.number().int().min(1).max(24 * 60), note: optionalText(500) });
export const timeReviewSchema = z.object({ ids: z.array(uuid).min(1).max(2000), approve: z.boolean(), reason: optionalText(500) });
export const allocationSchema = z
  .object({ id: uuid.nullish(), projectId: uuid, userId: uuid, startDate: isoDay, endDate: isoDay, percent: z.number().int().min(1).max(200), note: optionalText(200) })
  .refine((v) => v.endDate >= v.startDate, { message: "La fine precede l'inizio", path: ["endDate"] });
export const TIME_STATUS_LABEL: Record<string, string> = { submitted: "Da approvare", approved: "Approvata", rejected: "Respinta" };
export const commentSchema = z.object({ taskId: uuid, body: z.string().trim().min(1).max(20_000), mentions: z.array(uuid).max(50).default([]) });

export const sprintInputSchema = z
  .object({ projectId: uuid, title: z.string().trim().min(1).max(120), goal: optionalText(500), startDate: isoDay, endDate: isoDay })
  .refine(range, rangeErr);
export const releaseInputSchema = z.object({
  projectId: uuid,
  version: z.string().trim().min(1).max(80),
  name: z.string().trim().min(1).max(200),
  description: optionalText(5000),
  date: isoDay,
  status: z.enum(RELEASE_STATUSES).default("PLANNED"),
  channel: z.enum(RELEASE_CHANNELS).default("MINOR"),
  ownerId: uuid.nullable().optional(),
});
export const requestInputSchema = z.object({
  projectId: uuid,
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(20_000).default(""),
  priority: z.enum(TASK_PRIORITIES).nullable().optional(),
  /** stima dell'assistente: quanto la richiesta è chiara e realizzabile così com'è (0-100) */
  confidencePercent: z.number().int().min(0).max(100).optional(),
  /** conversazione con l'assistente che ha portato alla richiesta */
  messages: z.array(z.object({ role: z.enum(["user", "assistant"]), content: z.string().max(5_000) })).max(30).optional(),
});
export const requestReviewSchema = z.object({
  id: uuid,
  decision: z.enum(["approve", "reject"]),
  rejectionReason: optionalText(1000),
  startDate: isoDay.optional(),
  endDate: isoDay.optional(),
  estimatedHours: z.number().min(0).max(10_000).optional(),
});

/** Giorni lavorativi tra due date ISO (inclusi), per durate e Gantt. */
export function workingDaysBetween(start: string, end: string, weekMode: "FULL_WEEK" | "WORK_WEEK" = "WORK_WEEK"): number {
  const s = new Date(`${start}T00:00:00Z`);
  const e = new Date(`${end}T00:00:00Z`);
  let n = 0;
  for (let d = s; d <= e; d = new Date(d.getTime() + 86_400_000)) {
    const wd = d.getUTCDay();
    if (weekMode === "FULL_WEEK" || (wd !== 0 && wd !== 6)) n++;
  }
  return n;
}

/** Margine previsto: (ricavo venduto − costo ore usate) / ricavo. */
/**
 * Margine previsto di un progetto: ricavo delle ore vendute meno il costo delle
 * ore usate. costCents = costo reale del personale (nucleo Persone) se noto;
 * altrimenti si stima al 60% della tariffa.
 */
export function projectMargin(opts: { soldMinutes: number; usedMinutes: number; rateCents: number; costRateCents?: number; costCents?: number }) {
  const revenue = (opts.soldMinutes / 60) * opts.rateCents;
  const cost = opts.costCents ?? (opts.usedMinutes / 60) * (opts.costRateCents ?? Math.round(opts.rateCents * 0.6));
  if (revenue <= 0) return { revenueCents: 0, costCents: Math.round(cost), marginPercent: null as number | null };
  return { revenueCents: Math.round(revenue), costCents: Math.round(cost), marginPercent: Math.round(((revenue - cost) / revenue) * 100) };
}

// ── assistente di progetto (da Planner) ──────────────────────────────
export const aiChatMessageSchema = z.object({ role: z.enum(["user", "assistant"]), content: z.string().trim().min(1).max(5_000) });

/** Piano proposto dall'assistente: task nuovi, modifiche a task esistenti, nuove release. */
export const planCreateSchema = z.object({
  key: z.string().trim().min(1).max(80),
  parentKey: z.string().trim().min(1).max(80).nullable(),
  title: z.string().trim().min(1).max(200),
  description: z.string().max(5_000),
  startDate: isoDay,
  endDate: isoDay,
  priority: z.enum(TASK_PRIORITIES).nullable(),
  estimatedHours: z.number().min(0).max(10_000),
  assigneeId: uuid.nullable(),
  sprintId: uuid.nullable(),
  releaseId: uuid.nullable(),
  releaseKey: z.string().trim().min(1).max(80).nullable(),
});
export const planUpdateSchema = z.object({
  taskId: uuid,
  expectedUpdatedAt: z.string().min(10).max(40),
  /** null = invariato */
  title: z.string().trim().min(1).max(200).nullable(),
  status: z.enum(TASK_STATUSES).nullable(),
  startDate: isoDay.nullable(),
  endDate: isoDay.nullable(),
  priority: z.enum(TASK_PRIORITIES).nullable(),
  estimatedHours: z.number().min(0).max(10_000).nullable(),
  assigneeId: uuid.nullable(),
  sprintId: uuid.nullable(),
  releaseId: uuid.nullable(),
  reason: z.string().max(300),
});
export const planReleaseSchema = z.object({
  key: z.string().trim().min(1).max(80),
  version: z.string().trim().min(1).max(40),
  name: z.string().trim().min(1).max(200),
  date: isoDay,
  channel: z.enum(RELEASE_CHANNELS),
});
export const planProposalSchema = z.object({
  summary: z.string().trim().min(1).max(1_000),
  creates: z.array(planCreateSchema).max(30),
  updates: z.array(planUpdateSchema).max(30),
  releases: z.array(planReleaseSchema).max(5),
});
export type PlanProposal = z.infer<typeof planProposalSchema>;
export const planGenerateSchema = z.object({ projectId: uuid, messages: z.array(aiChatMessageSchema).min(1).max(20), current: planProposalSchema.nullable().optional() });
export const planCommitSchema = z.object({ projectId: uuid, proposal: planProposalSchema });

export const requestDraftSchema = z.object({ projectId: uuid, messages: z.array(aiChatMessageSchema).min(1).max(20) });
export const taskDraftSchema = z.object({ projectId: uuid, prompt: z.string().trim().min(3).max(2_000) });
